import React, { useState } from 'react';
import { Search, ChevronRight, ChevronDown, BookOpen, Video, FileText, MessageCircle, Users, Building, Target, Calendar, BarChart3, Settings, CreditCard } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const HelpDocumentation = () => {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const helpSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: BookOpen,
      articles: [
        {
          id: 'welcome-guide',
          title: 'Welcome to Bissa.ai',
          content: `
            <h2>Welcome to Bissa.ai - Where Heritage Meets AI</h2>
            <p>Just as the Bissa people of Burkina Faso built thriving communities through trust and connection, Bissa.ai empowers your business with AI-driven insights that strengthen every relationship.</p>
            
            <h3>What You Can Do</h3>
            <ul>
              <li><strong>Manage Contacts:</strong> Keep track of all your business relationships in one place</li>
              <li><strong>Track Deals:</strong> Visualize your sales pipeline and never miss opportunities</li>
              <li><strong>Organize Companies:</strong> Maintain detailed records of business partners and prospects</li>
              <li><strong>Stay Productive:</strong> Use tasks and notes to stay on top of your work</li>
              <li><strong>Gain Insights:</strong> View analytics and reports to improve your performance</li>
            </ul>

            <h3>Your First Steps</h3>
            <ol>
              <li>Complete your profile setup</li>
              <li>Add your first contact</li>
              <li>Create a company record</li>
              <li>Set up your first deal</li>
              <li>Explore the dashboard</li>
            </ol>
          `,
          type: 'guide'
        },
        {
          id: 'quick-start',
          title: '5-Minute Quick Start Guide',
          content: `
            <h2>Get Up and Running in 5 Minutes</h2>
            
            <h3>Step 1: Add Your First Contact (1 min)</h3>
            <p>Go to Contacts → Add Contact. Fill in basic information like name, email, and company.</p>
            
            <h3>Step 2: Create a Company (1 min)</h3>
            <p>Visit Companies → Add Company. Add details about businesses you work with.</p>
            
            <h3>Step 3: Set Up Your First Deal (2 mins)</h3>
            <p>Navigate to Deals and create a new opportunity. Link it to a contact and company.</p>
            
            <h3>Step 4: Explore the Dashboard (1 min)</h3>
            <p>Review your overview, check statistics, and get familiar with the navigation.</p>
            
            <div class="bg-blue-50 p-4 rounded-lg mt-4">
              <p><strong>💡 Pro Tip:</strong> Use the search bar in the header to quickly find contacts, companies, or deals.</p>
            </div>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'contacts',
      title: 'Contact Management',
      icon: Users,
      articles: [
        {
          id: 'adding-contacts',
          title: 'Adding and Organizing Contacts',
          content: `
            <h2>Contact Management Made Simple</h2>
            
            <h3>Adding New Contacts</h3>
            <p>There are multiple ways to add contacts to your Bissa.ai account:</p>
            <ul>
              <li><strong>Manual Entry:</strong> Click "Add Contact" and fill in the details</li>
              <li><strong>Quick Add:</strong> Use the + button for faster entry</li>
              <li><strong>CSV Import:</strong> Bulk import from spreadsheets</li>
            </ul>
            
            <h3>Contact Information Fields</h3>
            <ul>
              <li><strong>Basic Info:</strong> Name, email, phone number</li>
              <li><strong>Company Details:</strong> Company name, job title</li>
              <li><strong>Status:</strong> Lead, prospect, customer, etc.</li>
              <li><strong>Tags:</strong> Custom labels for organization</li>
              <li><strong>Notes:</strong> Additional context and history</li>
            </ul>
            
            <h3>Contact Status Types</h3>
            <ul>
              <li><strong>Lead:</strong> Initial interest or inquiry</li>
              <li><strong>Prospect:</strong> Qualified potential customer</li>
              <li><strong>Customer:</strong> Active paying customer</li>
              <li><strong>Partner:</strong> Business partner or collaborator</li>
            </ul>
          `,
          type: 'guide'
        },
        {
          id: 'contact-search',
          title: 'Searching and Filtering Contacts',
          content: `
            <h2>Find Contacts Quickly</h2>
            
            <h3>Search Options</h3>
            <p>Use the search bar to find contacts by:</p>
            <ul>
              <li>Name (first or last)</li>
              <li>Email address</li>
              <li>Company name</li>
              <li>Tags</li>
            </ul>
            
            <h3>Filter by Status</h3>
            <p>Use the status filter to view:</p>
            <ul>
              <li>All contacts</li>
              <li>Leads only</li>
              <li>Prospects</li>
              <li>Customers</li>
              <li>Partners</li>
            </ul>
            
            <h3>Advanced Tips</h3>
            <ul>
              <li>Search is case-insensitive</li>
              <li>Partial matches work (e.g., "john" finds "Johnson")</li>
              <li>Combine search with filters for better results</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'companies',
      title: 'Company Management',
      icon: Building,
      articles: [
        {
          id: 'company-setup',
          title: 'Managing Company Records',
          content: `
            <h2>Track Your Business Relationships</h2>
            
            <h3>Company Information</h3>
            <p>Keep detailed records of businesses you work with:</p>
            <ul>
              <li><strong>Company Name:</strong> Official business name</li>
              <li><strong>Domain:</strong> Website URL</li>
              <li><strong>Industry:</strong> Business sector or category</li>
              <li><strong>Size:</strong> Employee count or revenue range</li>
              <li><strong>Contact Info:</strong> Phone, address, email</li>
            </ul>
            
            <h3>Linking Companies to Contacts</h3>
            <p>When adding contacts, you can:</p>
            <ul>
              <li>Select from existing companies</li>
              <li>Create new companies on the fly</li>
              <li>Link multiple contacts to one company</li>
            </ul>
            
            <h3>Company Notes and History</h3>
            <p>Use the notes section to track:</p>
            <ul>
              <li>Meeting summaries</li>
              <li>Project details</li>
              <li>Partnership terms</li>
              <li>Communication history</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'deals',
      title: 'Sales & Deals',
      icon: Target,
      articles: [
        {
          id: 'deal-pipeline',
          title: 'Managing Your Sales Pipeline',
          content: `
            <h2>Visualize Your Sales Process</h2>
            
            <h3>Deal Stages</h3>
            <p>Track deals through these stages:</p>
            <ol>
              <li><strong>Lead:</strong> Initial interest or inquiry</li>
              <li><strong>Qualified:</strong> Confirmed as potential opportunity</li>
              <li><strong>Proposal:</strong> Formal proposal submitted</li>
              <li><strong>Negotiation:</strong> Terms and pricing discussions</li>
              <li><strong>Closed Won:</strong> Successfully completed deal</li>
              <li><strong>Closed Lost:</strong> Deal didn't proceed</li>
            </ol>
            
            <h3>Deal Information</h3>
            <ul>
              <li><strong>Deal Title:</strong> Descriptive name for the opportunity</li>
              <li><strong>Value:</strong> Potential revenue amount</li>
              <li><strong>Contact:</strong> Primary contact for this deal</li>
              <li><strong>Company:</strong> Associated business</li>
              <li><strong>Stage:</strong> Current position in pipeline</li>
              <li><strong>Expected Close:</strong> Anticipated completion date</li>
            </ul>
            
            <h3>Moving Deals Forward</h3>
            <p>Use drag-and-drop to:</p>
            <ul>
              <li>Move deals between stages</li>
              <li>Update deal status quickly</li>
              <li>Visualize pipeline flow</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'tasks',
      title: 'Tasks & Productivity',
      icon: Calendar,
      articles: [
        {
          id: 'task-management',
          title: 'Stay Organized with Tasks',
          content: `
            <h2>Never Miss Important Follow-ups</h2>
            
            <h3>Creating Tasks</h3>
            <p>Add tasks to track:</p>
            <ul>
              <li>Follow-up calls</li>
              <li>Email reminders</li>
              <li>Meeting preparation</li>
              <li>Proposal deadlines</li>
              <li>Contract reviews</li>
            </ul>
            
            <h3>Task Properties</h3>
            <ul>
              <li><strong>Title:</strong> What needs to be done</li>
              <li><strong>Description:</strong> Additional details</li>
              <li><strong>Due Date:</strong> When it should be completed</li>
              <li><strong>Priority:</strong> High, Medium, or Low</li>
              <li><strong>Status:</strong> Pending, In Progress, Completed</li>
              <li><strong>Related Contact:</strong> Link to specific contacts</li>
            </ul>
            
            <h3>Task Organization</h3>
            <p>Filter and sort tasks by:</p>
            <ul>
              <li>Due date (overdue, today, this week)</li>
              <li>Priority level</li>
              <li>Status</li>
              <li>Associated contact or company</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'analytics',
      title: 'Analytics & Reports',
      icon: BarChart3,
      articles: [
        {
          id: 'dashboard-overview',
          title: 'Understanding Your Dashboard',
          content: `
            <h2>Make Data-Driven Decisions</h2>
            
            <h3>Key Metrics</h3>
            <p>Your dashboard shows:</p>
            <ul>
              <li><strong>Total Contacts:</strong> Number of people in your network</li>
              <li><strong>Companies:</strong> Business relationships tracked</li>
              <li><strong>Active Deals:</strong> Opportunities in your pipeline</li>
              <li><strong>Total Deal Value:</strong> Potential revenue in pipeline</li>
              <li><strong>Revenue:</strong> Closed won deal value</li>
              <li><strong>Conversion Rate:</strong> Success rate of your deals</li>
            </ul>
            
            <h3>Understanding Conversion Rate</h3>
            <p>Calculated as: (Closed Won Deals / Total Deals) × 100</p>
            <p>This helps you understand your sales effectiveness.</p>
            
            <h3>Deal Value Tracking</h3>
            <ul>
              <li><strong>Pipeline Value:</strong> Sum of all open deals</li>
              <li><strong>Won Revenue:</strong> Actual closed business</li>
              <li><strong>Lost Value:</strong> Deals that didn't close</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Subscriptions',
      icon: CreditCard,
      articles: [
        {
          id: 'subscription-plans',
          title: 'Understanding Subscription Plans',
          content: `
            <h2>Choose the Right Plan for Your Business</h2>
            
            <h3>Available Plans</h3>
            
            <h4>Free Plan - $0/month</h4>
            <ul>
              <li>100 contacts</li>
              <li>1 user</li>
              <li>Basic CRM features</li>
              <li>50 tasks per month</li>
              <li>Community support</li>
            </ul>
            
            <h4>Growth Plan - $15/month</h4>
            <ul>
              <li>5,000 contacts</li>
              <li>3 users</li>
              <li>500 tasks per month</li>
              <li>25 campaigns</li>
              <li>Email integration</li>
              <li>Email support</li>
            </ul>
            
            <h4>Pro Plan - $35/month</h4>
            <ul>
              <li>10,000 contacts</li>
              <li>10 users</li>
              <li>Everything in Growth</li>
              <li>API access</li>
              <li>Advanced reports</li>
              <li>Priority support</li>
            </ul>
            
            <h4>Business Plan - $79/month</h4>
            <ul>
              <li>Unlimited contacts</li>
              <li>Unlimited users</li>
              <li>Everything in Pro</li>
              <li>White-label option</li>
              <li>Custom integrations</li>
              <li>Dedicated support</li>
            </ul>
            
            <h3>Upgrading Your Plan</h3>
            <p>You can upgrade anytime from the Billing page in Settings. Changes take effect immediately, and you'll be charged prorated amounts.</p>
          `,
          type: 'guide'
        },
        {
          id: 'usage-limits',
          title: 'Understanding Usage Limits',
          content: `
            <h2>How Usage Limits Work</h2>
            
            <h3>Contact Limits</h3>
            <p>Each plan has a limit on the total number of contacts you can store:</p>
            <ul>
              <li>When you reach your limit, you'll need to upgrade to add more</li>
              <li>Deleted contacts don't count toward your limit</li>
              <li>The system will warn you when approaching your limit</li>
            </ul>
            
            <h3>Task Limits</h3>
            <p>Monthly limits reset on your billing date:</p>
            <ul>
              <li>Completed tasks count toward your monthly limit</li>
              <li>Deleted tasks still count for that month</li>
              <li>Limits reset each billing cycle</li>
            </ul>
            
            <h3>Campaign Limits</h3>
            <p>Total number of marketing campaigns you can create:</p>
            <ul>
              <li>Both active and completed campaigns count</li>
              <li>Deleted campaigns free up space</li>
              <li>Draft campaigns count toward your limit</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    },
    {
      id: 'settings',
      title: 'Settings & Configuration',
      icon: Settings,
      articles: [
        {
          id: 'account-settings',
          title: 'Managing Your Account',
          content: `
            <h2>Customize Your Bissa.ai Experience</h2>
            
            <h3>Profile Settings</h3>
            <p>Update your personal information:</p>
            <ul>
              <li>Name and email address</li>
              <li>Profile photo</li>
              <li>Time zone preferences</li>
              <li>Language settings</li>
            </ul>
            
            <h3>Notification Preferences</h3>
            <p>Control when you receive notifications:</p>
            <ul>
              <li>Email notifications for new leads</li>
              <li>Task due date reminders</li>
              <li>Deal stage change alerts</li>
              <li>Weekly summary reports</li>
            </ul>
            
            <h3>Security Settings</h3>
            <ul>
              <li>Change password</li>
              <li>Two-factor authentication</li>
              <li>Active sessions management</li>
              <li>Login history</li>
            </ul>
            
            <h3>API Keys</h3>
            <p>For Pro and Business plans:</p>
            <ul>
              <li>Generate API keys for integrations</li>
              <li>Manage key permissions</li>
              <li>Monitor API usage</li>
            </ul>
          `,
          type: 'guide'
        }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const filteredSections = helpSections.map(section => ({
    ...section,
    articles: section.articles.filter(article => 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(section => section.articles.length > 0 || searchTerm === '');

  if (selectedArticle) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => setSelectedArticle(null)}
            className="text-bissa-blue hover:text-bissa-gold transition-colors mb-4 font-medium"
          >
            ← Back to Help Center
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedArticle.title}</h1>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div 
            className="prose prose-lg max-w-none"
            dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-gradient-to-br from-bissa-blue to-bissa-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Help Center</h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Find answers, learn new features, and get the most out of Bissa.ai
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-8 max-w-md mx-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search help articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-bissa-blue focus:border-transparent"
        />
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
        <div className="bg-gradient-to-r from-bissa-blue-50 to-bissa-gold-50 p-6 rounded-lg border border-bissa-blue-200">
          <Video className="w-8 h-8 text-bissa-blue mb-3" />
          <h3 className="font-semibold text-bissa-blue mb-2">Video Tutorials</h3>
          <p className="text-sm text-bissa-blue-700">Watch step-by-step guides</p>
        </div>
        
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <MessageCircle className="w-8 h-8 text-green-600 mb-3" />
          <h3 className="font-semibold text-green-800 mb-2">Contact Support</h3>
          <p className="text-sm text-green-700">Get help from our team</p>
        </div>
        
        <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
          <Users className="w-8 h-8 text-purple-600 mb-3" />
          <h3 className="font-semibold text-purple-800 mb-2">Community</h3>
          <p className="text-sm text-purple-700">Connect with other users</p>
        </div>
      </div>

      {/* Help Articles */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
            <nav className="space-y-2">
              {filteredSections.map((section) => {
                const IconComponent = section.icon;
                const isExpanded = expandedSections.includes(section.id);
                
                return (
                  <div key={section.id}>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className="w-4 h-4 text-gray-500" />
                        <span className="text-sm font-medium">{section.title}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-7 mt-1 space-y-1">
                        {section.articles.map((article) => (
                          <button
                            key={article.id}
                            onClick={() => setSelectedArticle(article)}
                            className="block w-full text-left p-2 text-sm text-gray-600 hover:text-bissa-blue hover:bg-bissa-blue-50 rounded transition-colors"
                          >
                            {article.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="space-y-6">
            {filteredSections.map((section) => {
              const IconComponent = section.icon;
              
              return (
                <div key={section.id} className="bg-white rounded-lg shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-6 h-6 text-bissa-blue" />
                      <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid gap-4">
                      {section.articles.map((article) => (
                        <button
                          key={article.id}
                          onClick={() => setSelectedArticle(article)}
                          className="text-left p-4 border border-gray-200 rounded-lg hover:border-bissa-blue hover:shadow-sm transition-all group"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-medium text-gray-900 group-hover:text-bissa-blue transition-colors">
                                {article.title}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                {article.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
                              </p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-bissa-blue transition-colors" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpDocumentation;